/**
 * useInvoiceFormState – helpers
 *
 * Pure builders that derive initial state from `initialData`. Kept module-scoped
 * so the hook initializer stays small.
 */

import type { Address } from '@/gen/proto'

import {
  generateId,
  createEmptyLineItem,
  formatQtyDisplay,
  type LineItem,
} from './invoice-form-helpers'
import { fromSmallestUnit } from '../../../utils/formatters'

import type { AddressSnapshot } from './useInvoiceFormState-types'
import type { Invoice, InvoiceType } from '../../../types/billing'

// ─────────────────────────────────────────────────────────────────────────────
// Address snapshot
// ─────────────────────────────────────────────────────────────────────────────

export function buildAddressSnapshot(src: Partial<Address> | undefined): AddressSnapshot {
  return {
    line1: src?.line1 ?? '',
    line2: src?.line2 ?? '',
    city: src?.city ?? '',
    state: src?.state ?? '',
    stateCode: src?.stateCode ?? '',
    postalCode: src?.postalCode ?? '',
    country: src?.country ?? '',
    countryCode: src?.countryCode ?? '',
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Line items
// ─────────────────────────────────────────────────────────────────────────────

type RawLineItem = NonNullable<Partial<Invoice>['items']>[number]

function buildLineItemIdentity(item: RawLineItem): Pick<LineItem, 'id' | 'itemId' | 'accountId' | 'itemCode' | 'description' | 'hsnCode' | 'sacCode' | 'unit' | 'gstRate'> {
  return {
    id: item.id ?? generateId(),
    itemId: item.itemId ?? '',
    accountId: item.accountId ?? '',
    itemCode: item.itemCode ?? '',
    description: item.description ?? '',
    hsnCode: item.hsnCode ?? '',
    sacCode: item.sacCode ?? '',
    unit: item.unit ?? 'NOS',
    gstRate: resolveGstRate(item),
  }
}

function buildLineItemAmounts(item: RawLineItem): Pick<LineItem, 'taxableAmount' | 'cgstAmount' | 'sgstAmount' | 'igstAmount' | 'totalAmount'> {
  return {
    taxableAmount: item.taxableAmount ?? 0,
    cgstAmount: item.cgstAmount ?? 0,
    sgstAmount: item.sgstAmount ?? 0,
    igstAmount: item.igstAmount ?? 0,
    totalAmount: item.totalAmount ?? 0,
  }
}

function buildLineItem(item: RawLineItem, currency: string): LineItem {
  return {
    ...buildLineItemIdentity(item),
    quantity: formatQtyDisplay(item.quantity?.toString() ?? '1', item.unit ?? ''),
    unitPrice: fromSmallestUnit(item.unitPrice ?? 0, currency).toString(),
    discountPercent: item.discountPercent?.toString() ?? '0',
    ...buildLineItemAmounts(item),
  }
}

export function buildInitialLineItems(
  initialData: Partial<Invoice> | undefined,
  baseCurrency: string,
): LineItem[] {
  if (!initialData?.items || initialData.items.length === 0) {
    return [createEmptyLineItem()]
  }
  const currency = initialData.currency ?? baseCurrency
  return initialData.items.map((item) => buildLineItem(item, currency))
}

function resolveGstRate(item: {
  cgstRate?: number | string
  sgstRate?: number | string
  igstRate?: number | string
}): string {
  const cgst = parseFloat(String(item.cgstRate ?? 0))
  const sgst = parseFloat(String(item.sgstRate ?? 0))
  const igst = parseFloat(String(item.igstRate ?? 0))
  if (cgst > 0 || sgst > 0) { return (cgst + sgst).toString() }
  if (igst > 0) { return igst.toString() }
  return '0'
}

// ─────────────────────────────────────────────────────────────────────────────
// Reconstructed party address (called from useMemo)
// ─────────────────────────────────────────────────────────────────────────────

function fromExplicitPartyAddress(
  src: Address,
  fallbackType: string,
  partyGstin: string | undefined,
  partyVendorCode: string | undefined,
): Address {
  return {
    ...src,
    type: src.type ?? fallbackType,
    gstin: src.gstin ?? partyGstin ?? '',
    companyCode: src.companyCode ?? partyVendorCode ?? '',
  }
}

const LEGACY_FIELD_MAP: Record<string, keyof Partial<Invoice>> = {
  line1: 'partyAddressLine1',
  line2: 'partyAddressLine2',
  city: 'partyCity',
  state: 'partyState',
  stateCode: 'partyStateCode',
  postalCode: 'partyPostalCode',
  country: 'partyCountry',
  countryCode: 'partyCountryCode',
  gstin: 'partyGstin',
  companyCode: 'partyVendorCode',
}

function fromLegacyPartyFields(initialData: Partial<Invoice>): Address {
  const fields: Record<string, string> = {}
  for (const [target, source] of Object.entries(LEGACY_FIELD_MAP)) {
    const value = initialData[source]
    fields[target] = typeof value === 'string' ? value : ''
  }
  return {
    id: '',
    label: 'billing',
    type: 'billing',
    isPrimary: false,
    ...fields,
  } as Address
}

export function buildReconstructedPartyAddress(
  initialData: Partial<Invoice> | undefined,
): Address | null {
  if (!initialData) { return null }

  if (initialData.partyBillingAddress && typeof initialData.partyBillingAddress === 'object') {
    return fromExplicitPartyAddress(
      initialData.partyBillingAddress as Address,
      'billing',
      initialData.partyGstin,
      initialData.partyVendorCode,
    )
  }

  if (initialData.partyAddress && typeof initialData.partyAddress === 'object') {
    return fromExplicitPartyAddress(
      initialData.partyAddress as Address,
      'billing',
      initialData.partyGstin,
      initialData.partyVendorCode,
    )
  }

  if (initialData.partyAddressLine1 ?? initialData.partyCity) {
    return fromLegacyPartyFields(initialData)
  }

  return null
}

export function buildReconstructedShipToAddress(
  initialData: Partial<Invoice> | undefined,
): Address | null {
  if (!initialData) { return null }
  if (initialData.shipToAddress && typeof initialData.shipToAddress === 'object') {
    const src = initialData.shipToAddress as Address
    return {
      ...src,
      type: src.type ?? 'shipping',
      gstin: src.gstin ?? initialData.partyGstin ?? '',
      companyCode: src.companyCode ?? '',
    }
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

export function defaultInvoiceType(
  initialData: Partial<Invoice> | undefined,
  isVendor: boolean,
  defaultDeliveryType: string | undefined,
): InvoiceType {
  if (initialData?.invoiceType) { return initialData.invoiceType }
  if (isVendor) { return 'vendor_bill' }
  if (defaultDeliveryType === 'export' || defaultDeliveryType === 'sez') { return 'export' }
  return 'b2b'
}

export function defaultExportType(
  initialData: Partial<Invoice> | undefined,
  businessType: string | undefined,
): 'goods' | 'services' {
  if (initialData?.exportType) { return initialData.exportType }
  if (businessType === 'service' || businessType === 'digital') { return 'services' }
  return 'goods'
}
